import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegHospital } from 'react-icons/fa';
import { Col, Empty, Input } from 'antd';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { Spinner } from 'reactstrap';
import styled from '@emotion/styled';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

const { Search } = Input;

interface Provider {
  distance_units: string;
  distance: number;
  postcode: string;
  region: string;
  addline1: string;
  addline2: string;
  addline3?: string;
  addline4?: string;
  hospital_name: string;
  service_type_codes: string;
  group_name: string;
  hospital_id?: number;
  collectionTypes?: string;
}

interface ProviderCardProps {
  selected: boolean;
  item: Provider;
  onProviderSelected?: (p: Provider) => void;
}

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
`;

// @ts-ignore
const ProviderCard: React.FC<ProviderCardProps> = ({ selected, item, onProviderSelected = () => {} }) => {
  const { t } = useTranslation('common');
  return (
    <Col
      style={{ display: 'flex', flexDirection: 'row', marginBlock: 10 }}
      onClick={() => onProviderSelected(item)}
      className={`provider ${selected ? 'selected' : 'unselected'}`}
    >
      <div className="provider-left">
        <FaRegHospital className="hospital-icon" style={{ fontSize: '2em' }} />
      </div>
      <div className="provider-right" style={{ width: '90%' }}>
        <strong>{item.group_name}</strong>
        <div className="procedure">{item.service_type_codes}</div>
        {item.hospital_name}
        <br />
        {item.addline1},{item.addline2 ? `${item.addline2}, ` : ''}
        {item.addline3 ? `${item.addline3}, ` : ''}
        {item.addline4 ? `${item.addline4}, ` : ''}
        {item.region ? `${item.region}, ` : ''}
        {item.postcode}
        <br />
        {item.collectionTypes && (
          <>
            {t('Available collection types:')}
            {item.collectionTypes}
            <br />
          </>
        )}
        <div className="distance">{`${item.distance} ${item.distance_units}`}</div>
      </div>
    </Col>
  );
};

interface ChooseProviderProps {
  providers: Provider[];
  onProviderSelected?: (p: Provider) => void;
  initialPostcode?: string;
  onSearch?: (s: string) => void;
  errorMessage?: string | null;
  isDataLoading: boolean;
  onLoadMore: () => void;
  hasMoreData?: boolean;
}

export const ChooseProvider: React.FC<ChooseProviderProps> = ({
  providers,
  onProviderSelected = () => {},
  onSearch = () => {},
  initialPostcode,
  errorMessage,
  isDataLoading,
  onLoadMore,
  hasMoreData
}) => {
  const { t } = useTranslation('common');
  const lastProviderCardRef = useInfiniteScroll<HTMLDivElement>({
    onLoadMore,
    enabled: hasMoreData && !isDataLoading
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <label className="search-provider">
        <span>Provider&nbsp;Location:&nbsp;</span>
        <Search placeholder={t('postcode')} defaultValue={initialPostcode} onSearch={onSearch} />
      </label>

      <AlertMessage message={errorMessage} visibility={!!errorMessage} />
      <i style={{ textAlign: 'right', margin: '10px 0' }}>
        {t('Please note: Clinics marked with an asterisk (*) are temporary clinics')}
      </i>

      {providers.map((provider, index) => {
        const isLast = index === providers.length - 1;
        return (
          <div key={provider.hospital_id} ref={isLast ? lastProviderCardRef : null}>
            <ProviderCard selected={false} item={provider} onProviderSelected={onProviderSelected} />
          </div>
        );
      })}

      {providers.length === 0 && !isDataLoading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No providers found')} />
      )}

      {isDataLoading && (
        <LoaderWrapper>
          <Spinner size="xl" style={{ marginRight: '8px' }} />
          {t('Loading...')}
        </LoaderWrapper>
      )}
    </div>
  );
};
